import {
  Component,
  forwardRef,
  input,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'so-checkbox-ui',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './so-checkbox.component.html',
  styleUrl: './so-checkbox.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoCheckboxUI),
      multi: true
    }
  ]
})
export class SoCheckboxUI implements ControlValueAccessor {
  label = input<string>('');
  disabled = input<boolean>(false);
  size = input<number>(20);
  labelPosition = input<'left' | 'right'>('right');
  gap = input<string>('8px');
  fontSize = input<string>('14px');
  fontWeight = input<'normal' | 'bold' | '500' | '600'>('normal');
  textColor = input<string>('');
  truncateText = input<boolean>(false);
  maxLabelWidth = input<string>('300px');
  isInvalid = input<boolean>(false);
  indeterminate = input<boolean>(false);
  
  checked = input<boolean>(false);
  checkId = input<string>('');
  nameGroup = input<string>('');
  textAlign = input<'left' | 'right'>('left');
  direction = input<'row' | 'column'>('row');
  showIcon = input<boolean>(false);
  iconSrc = input<string>('');
  iconSize = input<number>(1);
  iconColor = input<string>('');
  showTooltip = input<boolean>(false);
  tooltipText = input<string>('');
  disabledTooltip = input<string>('');

  checkedChange = output<boolean>();
  checkboxChange = output<{ checked: boolean; element: HTMLInputElement }>();

  protected internalValue = signal<boolean>(false);
  protected internalIndeterminate = signal<boolean>(false);
  protected internalDisabled = signal<boolean>(false);

  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  protected effectiveDisabled = computed(() => 
    this.disabled() || this.internalDisabled()
  );
  
  protected effectiveLabel = computed(() => this.label());
  
  protected effectiveLabelPosition = computed(() => {
    const align = this.textAlign();
    if (align === 'right') return 'left';
    return this.labelPosition();
  });

  protected containerClass = computed(() => {
    const position = this.effectiveLabelPosition();
    const direction = this.direction();
    return {
      'label-left': position === 'left',
      'label-right': position === 'right',
      'direction-column': direction === 'column',
      'disabled': this.effectiveDisabled()
    };
  });

  protected checkboxSize = computed(() => `${this.size()}px`);

  protected labelStyle = computed(() => {
    const color = this.textColor();
    return {
      'color': color || 'var(--so-slate-700)',
      'font-size': this.fontSize(),
      'font-weight': this.fontWeight(),
      'max-width': this.truncateText() ? this.maxLabelWidth() : 'none'
    };
  });

  protected checkboxClass = computed(() => ({
    'checked': this.internalValue(),
    'invalid': this.isInvalid() && !this.effectiveDisabled(),
    'indeterminate': this.internalIndeterminate()
  }));

  protected showCheckIcon = computed(() => 
    this.internalValue() && !this.internalIndeterminate()
  );

  protected showIndeterminateIcon = computed(() => 
    this.internalIndeterminate()
  );

  constructor() {
    effect(() => {
      const checkedValue = this.checked();
      if (checkedValue !== undefined) {
        this.internalValue.set(!!checkedValue);
      }
    }, {  });

    effect(() => {
      const indeterminateValue = this.indeterminate();
      this.internalIndeterminate.set(!!indeterminateValue);
    }, {  });
  }

  writeValue(value: boolean | null): void {
    this.internalValue.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.internalDisabled.set(isDisabled);
  }

  onCheckboxClick(event: Event): void {
    if (this.effectiveDisabled()) return;

    event.stopPropagation();
    
    this.internalIndeterminate.set(false);
    const newValue = !this.internalValue();
    this.internalValue.set(newValue);
    
    this.onChange(newValue);
    this.onTouched();
    this.checkedChange.emit(newValue);
    this.checkboxChange.emit({
      checked: newValue,
      element: event.target as HTMLInputElement
    });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.onCheckboxClick(event);
    }
  }
}
